import { ReactNode } from "react";

import {
    faCheckCircle,
    faTriangleExclamation
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
    Toast as ChakraToast,
    Toaster as ChakraToaster,
    Flex,
    Portal,
    Spinner,
    Stack,
    createToaster
} from "@chakra-ui/react";

export interface PropTypes {
    title?: ReactNode;
    description?: ReactNode;
    status:
        | "info"
        | "warning"
        | "success"
        | "error"
        | "loading"
        | "neutral"
        | undefined;
}

export const toaster = createToaster({
    placement: "bottom",
    pauseOnPageIdle: false,
    offsets: { left: "20px", top: "20px", right: "20px", bottom: "32px" },
    duration: 5000
});

const Toast = () => {
    const getBorderColor = (status: string) => {
        switch (status) {
            case "success":
                return "var(--euka-colors-legacy-mint-green-500)";
            case "error":
                return "var(--euka-colors-legacy-danger-500)";
            default:
                return "var(--euka-colors-legacy-gray-500)";
        }
    };

    const getIcon = (status: string | undefined) => {
        switch (status) {
            case "error":
                return (
                    <FontAwesomeIcon
                        icon={faTriangleExclamation}
                        color="var(--euka-colors-legacy-danger-500)"
                        style={{ marginRight: 10 }}
                        data-testid="triangleIcon"
                    />
                );
            case "success":
                return (
                    <Flex
                        fontSize={24}
                        justifyContent="center"
                        alignItems="center"
                    >
                        <FontAwesomeIcon
                            icon={faCheckCircle}
                            color="var(--euka-colors-legacy-mint-green-500)"
                            style={{ marginRight: 10 }}
                            data-testid="successIcon"
                        />
                    </Flex>
                );
            default:
                return <ChakraToast.Indicator data-testid="alertIcon" />;
        }
    };

    const getBgColor = (type: string) => {
        switch (type) {
            case "success":
                return "legacy.success.100";
            case "error":
                return "legacy.danger.100";
            default:
                return "legacy.gray.50";
        }
    };

    // Map "loading" to "neutral" since Chakra UI v3 doesn't support "loading"
    // const alertStatus = status === "loading" ? "neutral" : status;

    return (
        <Portal>
            <ChakraToaster toaster={toaster} insetInline={{ mdDown: "4" }}>
                {toast => (
                    <ChakraToast.Root
                        width="80vw"
                        maxWidth="unset"
                        border={`1px solid ${getBorderColor(toast.type as string)}`}
                        backgroundColor={getBgColor(toast.type as string)}
                        borderRadius="8px"
                        data-tracking-id={toast.description}
                        shadow="rgba(12, 26, 75, 0.10), 0px 4px 20px -2px rgba(50, 50, 71, 0.08)"
                    >
                        {toast.type === "loading" ? (
                            <Spinner size="sm" color="legacy.deepBlue.500" />
                        ) : (
                            getIcon(toast?.type)
                        )}
                        <Stack gap="1" flex="1" maxWidth="100%">
                            {toast.title && (
                                <ChakraToast.Title
                                    color="legacy.ebonyClay.500"
                                    fontWeight="semibold"
                                    fontSize={16}
                                >
                                    {toast.title}
                                </ChakraToast.Title>
                            )}
                            {toast.description && (
                                <ChakraToast.Description
                                    fontFamily="secondary"
                                    color="legacy.ebonyClay.500"
                                    fontSize={16}
                                    fontWeight={500}
                                >
                                    {toast.description}
                                </ChakraToast.Description>
                            )}
                        </Stack>
                        {toast.action && (
                            <ChakraToast.ActionTrigger>
                                {toast.action.label}
                            </ChakraToast.ActionTrigger>
                        )}
                        {toast.meta?.closable && <ChakraToast.CloseTrigger />}
                    </ChakraToast.Root>
                )}
            </ChakraToaster>
        </Portal>
    );
};

export default Toast;
